import { useApolloClient } from '@apollo/react-hooks';
import RsvpSettings from '../../libs/component-rsvp-settings';
import { EventType } from '../../libs/component-rsvp-settings/EventTypes';
import { GuestWedding } from '../../libs/component-rsvp-settings/GuestWeddingTypes';
import { Household } from '../../libs/component-rsvp-settings/HouseholdTypes';
import { EventResponse, GuestWeddingResponse } from '../../libs/gds-api-client';
import { useLocation } from 'react-router-dom';
import { compose } from '@xo-union/react-css-modules';
import Modal from '@xo-union/tk-component-modals';
import gql from 'graphql-tag';
import produce from 'immer';
import React, { useContext, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import {
  EventsQuery,
  Maybe,
  useEventsQuery,
  useHouseholdQuery,
  useWeddingWebsiteQuery,
  WeddingWebsiteQuery,
} from '../../generated/graphql';
import { manageRsvpSettingsRoute, rsvpSettingsRoute, rsvpWorkflowRoute } from '../../helpers/routes';
import { useRsvpOncount } from '../../helpers/useRvspOnCount';
import { TrackingContext } from '../../TrackingContext';
import { weddingWebsiteQuery } from '../Dashboard';
import LinkButton from '../LinkButton';
import { useSeeWorkflow, useWorkflowUnstartedState } from '../RsvpPageCard/utils';
import { Event } from './EventList';
import { eventsQuery } from './graphql';
import { withTypename } from './helper';
import styles from './rsvpInfo.scss';
import { useFlipper } from '../../hooks/useFlipper';

interface Props {
  onEventModal: (event: Event) => void;
}

const EVENT_RR_EXP_CONTROL = 'control';
const EVENT_RR_EXP_VARIANT = 'variant';

export const householdQuery = gql`
  query householdQuery {
    households {
      id
      description
      rsvpNote
      coupleNote
      people {
        ...People
      }
      address {
        ...GDSAddress
      }
      answers {
        ...GDSAnswer
      }
    }
  }

  fragment GDSAnswer on GDS_Answer {
    optionId
    questionId
    text
  }

  fragment GDSAddress on GDS_Address {
    street1
    street2
    city
    state
    zip
    country
    fullAddress
  }

  fragment People on GDS_Person {
    id
    firstName
    lastName
    isLeader
    email
    phone
    preferences {
      emailOptOut
      smsOptOut
    }
    invitations {
      id
      eventId
      rsvp
      answers {
        ...GDSAnswer
      }
    }
  }
`;

export const getRsvpInfoCopy = (rsvpOnCount: number, isPrivateRsvp: boolean, households: any[]): string => {
  if (rsvpOnCount === 0) {
    return 'No one can RSVP on your site';
  }

  if (isPrivateRsvp !== true) {
    return 'Visitors to your website can RSVP';
  }

  if (households.length) {
    return 'Guests on your Guest List can RSVP';
  }

  return 'Once you add guests to your list, they can RSVP';
};

type RsvpPageCopy = {
  isPrivateRsvp: Maybe<boolean>;
  isPinProtected: boolean;
};

export const getRsvpPageCopy = ({ isPrivateRsvp, isPinProtected }: RsvpPageCopy): string => {
  if (isPinProtected) {
    return 'Anyone with the guest password can RSVP on your Website.';
  }

  if (isPrivateRsvp) {
    return 'Guests on your Guest List can RSVP on your Website.';
  }

  return 'Anyone can RSVP on your Website.';
};

const RsvpInfo: React.FC<Props> = ({ onEventModal }) => {
  const { track } = useContext(TrackingContext);
  const client = useApolloClient();
  const { pathname } = useLocation();
  const { data } = useEventsQuery();
  const { data: wwData } = useWeddingWebsiteQuery();
  const { data: hData } = useHouseholdQuery();
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const { isUnstartedState } = useWorkflowUnstartedState();
  const seeWorkflow = useSeeWorkflow();
  const preview = pathname.includes('preview');

  const { variant } = useFlipper(
    process.env.REACT_APP_FLIPPER_EVENT_RAIL_VENUES_LINK_EXP_ID as string,
    EVENT_RR_EXP_CONTROL
  );
  const isExperiment = variant === EVENT_RR_EXP_VARIANT;

  let rsvpSettings = null;

  const rsvpOnCount = useRsvpOncount();

  const onClose = () => setShowModal(false);

  const onSuccess = (data: GuestWeddingResponse) => {
    const cache = client.readQuery<WeddingWebsiteQuery>({ query: weddingWebsiteQuery })!;

    const updated = produce(cache, draft => {
      if (draft.guestWedding) {
        draft.guestWedding.isPrivateRsvp = data.isPrivateRsvp;
      }
    });

    client.writeQuery({
      query: weddingWebsiteQuery,
      data: updated,
    });
  };

  const onEventsUpdate = (events: EventResponse[]) => {
    const cache = client.readQuery<EventsQuery>({ query: eventsQuery })!;

    const updated = produce(cache, draft => {
      events.forEach(event => {
        if (draft.events && draft.events.length) {
          const index = draft.events?.findIndex(e => e?.id === event.id);
          if (index >= 0) {
            draft.events[index] = withTypename(event);
          }
        }
      });
    });

    client.writeQuery({ query: eventsQuery, data: { events: updated.events } });
  };

  const openEventModal = (eventId: string) => {
    const cache = client.readQuery<EventsQuery>({ query: eventsQuery })!;

    const event = cache.events?.find(e => e?.id === eventId);
    if (event) {
      onClose();
      onEventModal(event as Event);
    }
  };
  const openRSVPSettingsModal = () => {
    track('WWS Dashboard Interaction', {
      action: 'RSVP Settings Initiated',
      actionName: 'RSVP Settings Initiated',
    });
    history.push(rsvpSettingsRoute);
  };
  const openManageRSVPSettings = () => {
    track('WWS Dashboard Interaction', {
      action: 'Manage RSVP Settings Initiated',
      actionName: 'Manage RSVP Settings Initiated',
    });
    history.push(manageRsvpSettingsRoute(preview));
  };

  let rsvpCopy = '';

  if (data && wwData && hData) {
    rsvpSettings = showModal && (
      <Modal
        data-testid="rsvp-settings-modal"
        onClose={onClose}
        size="lg"
        classes={compose({ 'button-container': styles.hide })}
      >
        <RsvpSettings
          guestWedding={wwData?.guestWedding as GuestWedding}
          events={data.events as EventType[]}
          households={hData.households as Household[]}
          isPrivateRsvp={Boolean(wwData.guestWedding?.isPrivateRsvp)}
          wwsState={wwData.weddingWebsite.state || ''}
          onCancelClick={onClose}
          onEventsUpdate={onEventsUpdate}
          onSuccess={onSuccess}
          openEventModal={openEventModal}
          goToGuestList={(eventId: string) => {
            window.location.assign(`/our-guest-list?event=${eventId}`);
          }}
          isExperiment={isExperiment}
        />
      </Modal>
    );

    rsvpCopy = getRsvpInfoCopy(rsvpOnCount, Boolean(wwData.guestWedding?.isPrivateRsvp), hData.households || []);
  }

  // In Workflow experiment and have not started it
  if (seeWorkflow && isUnstartedState) {
    return (
      <div className={styles.container}>
        <span>Online RSVP isn't set up on your Website yet.</span>
        <Link data-testid="rsvp-info-cta" to={rsvpWorkflowRoute}>
          Explore Online RSVP
        </Link>
      </div>
    );
  }

  // Has RSVP Page visible
  if (wwData?.guestWedding && !wwData.guestWedding.rsvpPageHidden) {
    return (
      <>
        {rsvpSettings}
        <div className={styles.container}>
          <span>
            {getRsvpPageCopy({
              isPrivateRsvp: wwData.guestWedding.isPrivateRsvp,
              isPinProtected: Boolean(wwData.weddingWebsite.pinProtect && wwData.weddingWebsite.pin),
            })}
          </span>
          {!preview && (
            <a data-testid="rsvp-info-cta" href="#rsvp">
              View Attendance Summary
            </a>
          )}
        </div>
      </>
    );
  }

  // Has RSVP Page hidden
  if (wwData?.guestWedding && wwData.guestWedding.rsvpPageHidden) {
    return (
      <>
        {rsvpSettings}
        <div className={styles.container}>
          <span>Online RSVP is currently hidden on your Website.</span>
          <span>
            <LinkButton data-testid="rsvp-info-cta-edit-form" onClick={openRSVPSettingsModal}>
              Edit RSVP Form
            </LinkButton>
            <span className={styles.linkDivider}>|</span>
            <LinkButton data-testid="rsvp-info-cta-edit-settings" onClick={openManageRSVPSettings}>
              Turn on RSVP
            </LinkButton>
          </span>
        </div>
      </>
    );
  }

  // When not in RSVP as page experiment
  return (
    <>
      {rsvpSettings}
      <div className={styles.container}>
        <span>{rsvpCopy}</span>
        <LinkButton onClick={openRSVPSettingsModal}>Manage RSVP Settings</LinkButton>
      </div>
    </>
  );
};

export default RsvpInfo;
